/**
 * RESPONSIBILITY: catch browser-side failures and ship them to the server.
 *
 * The browser console is per-tab and gone on reload. A crash nobody was watching for
 * is a crash nobody knows happened — which is exactly the one worth having a record
 * of. Everything here exists to get that record into server.log.
 *
 * Nothing in this file may throw. Reporting an error must not be able to cause one.
 */
import { FRAMEWORK_ROUTES, ClientErrorKind, type ClientErrorReport } from '@framework/core';

/**
 * A render loop throws as fast as React can re-render. The log is a shared, finite
 * thing; a page that crashes in a loop must not be able to bury every other line.
 */
const MAX_PER_PAGE = 20;
let sent = 0;

/** The same failure repeats constantly. One line each is the whole point. */
const seen = new Set<string>();

/**
 * Never actionable, always present: a missing favicon, an extension's own exception
 * thrown in our frame, and the ResizeObserver notice every browser emits benignly.
 *
 * 'Script error.' is the cross-origin placeholder — by definition it carries no
 * message, no file and no line, so logging it only adds noise.
 */
const NOISE: RegExp[] = [
  /favicon/i,
  /^(chrome|moz|safari)-extension:/i,
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
];

const isNoise = (text: string): boolean => NOISE.some((re) => re.test(text));

/**
 * Did OUR code throw this?
 *
 * A page runs plenty of script that is not ours — extensions, injected trackers,
 * anything a corporate proxy rewrites in. Those throw into our frame and are not
 * ours to fix, so reporting them buries the ones that are.
 *
 * A frame from our own origin is the evidence. Absence of any file at all means the
 * browser withheld it (cross-origin), which is likewise not something we can act on.
 */
function isOurs(report: ClientErrorReport): boolean {
  const where = `${report.source ?? ''} ${report.stack ?? ''}`;
  return where.includes(window.location.origin);
}

function send(report: ClientErrorReport): void {
  if (isNoise(`${report.message} ${report.source ?? ''} ${report.stack ?? ''}`)) return;

  // A boundary catch and a rejected promise are ours by construction — the boundary
  // only wraps our tree, and the rejection came from our own awaited call. Only the
  // global window handler catches other people's code, so only it needs the check.
  if (report.kind === ClientErrorKind.Error && !isOurs(report)) return;

  const fingerprint = `${report.kind}|${report.message}|${report.source ?? ''}|${report.line ?? ''}`;
  if (seen.has(fingerprint)) return;
  if (sent >= MAX_PER_PAGE) return;

  seen.add(fingerprint);
  sent += 1;

  try {
    // Bare fetch, NOT the api() helper: that throws a typed ApiFailure on a non-2xx,
    // and an exception raised while reporting an exception is how you build a loop.
    // keepalive so a crash during unload still gets the report out of the door.
    void fetch(FRAMEWORK_ROUTES.clientErrors, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* Reporting is best-effort. It never breaks the page it is reporting on. */
  }
}

/** Used by the error boundary, which has an error object React handed it. */
export function reportClientError(
  error: unknown,
  kind: ClientErrorKind,
  extra: Partial<ClientErrorReport> = {},
): void {
  const e = error as Error | undefined;
  send({
    kind,
    message: e?.message ? String(e.message) : String(error),
    stack: e?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...extra,
  });
}

/**
 * Call once, as early as possible — anything that throws before this runs is lost.
 *
 * These two listeners are what catch the failures no boundary sees: async work,
 * event handlers, and anything outside React entirely.
 */
export function installClientErrorReporting(): void {
  window.addEventListener('error', (e) => {
    reportClientError(e.error ?? e.message, ClientErrorKind.Error, {
      source: e.filename,
      line: e.lineno,
      column: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    reportClientError(e.reason, ClientErrorKind.Rejection);
  });
}
